import { QueryFunction, QueryOptions, useQueries, useQuery } from '@tanstack/react-query';
import { useSiloWeb3 } from '@silo/web3';
import { ChecksumAddress, ClientProvider, ProtocolInstance } from '@silo/types';
import { isSiloReady } from '@silo/contract-utils';

type IsSiloReadyKey = [
  key: typeof isSiloReadyKey,
  clientProvider: ClientProvider,
  protocolInstance: ProtocolInstance,
  siloAddress: ChecksumAddress
];

export type IsSiloReadyData = {
  isReady: boolean;
  siloAddress: ChecksumAddress;
};

const isSiloReadyKey = 'isSiloReady';

export const useAreSilosReady = (siloAddresses: ChecksumAddress[], enabled = true) => {
  const { clientProvider, protocolInstance } = useSiloWeb3();

  return useQueries<QueryOptions<IsSiloReadyData, unknown, IsSiloReadyData, IsSiloReadyKey>[]>({
    queries: siloAddresses.map((siloAddress) => {
      return {
        // non null assertion, as the query is not enabled until
        // repository contracts are present
        queryKey: [isSiloReadyKey, clientProvider!, protocolInstance!, siloAddress],
        queryFn: queryIsSiloReady,
        enabled: !!clientProvider && !!protocolInstance && enabled,
        onError: () => {
          // TODO: implement error handling (not necessarily notifying user)
        },
      };
    }),
  });
};

const queryIsSiloReady: QueryFunction<IsSiloReadyData, IsSiloReadyKey> = ({ queryKey }) => {
  const [, clientProvider, protocolInstance, siloAddress] = queryKey;
  return isSiloReady(clientProvider, protocolInstance, siloAddress).then((isReady) => ({
    isReady,
    siloAddress,
  }));
};

interface Options {
  suspense?: boolean;
}

/**
 * This implementation is copied from the `useQueryDecoratedSilos` hook instead
 * of reusing it because react-query does not currently support suspense with
 * useQueries: https://github.com/TanStack/query/discussions/3422
 */
export const useIsSiloReady = (siloAddress: ChecksumAddress | undefined, options?: Options) => {
  const { clientProvider, protocolInstance } = useSiloWeb3();

  return useQuery([isSiloReadyKey, clientProvider!, protocolInstance!, siloAddress!], queryIsSiloReady, {
    enabled: !!clientProvider && !!protocolInstance && !!siloAddress,
    onError: () => {
      // TODO: implement error handling (not necessarily notifying user)
    },
    ...options,
  });
};
